import Input from 'Common/Components/Input';
import CommonModal from 'Common/Components/Modal/CommonModal';
import { ModalButton } from 'Common/Components/Modal/ModalButton';
import useNotification from 'Common/usePromiseNotification';
import { callChangePassword } from 'helpers/api_helper';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';

type ChangePasswordModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

type ChangePasswordForm = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const initialForm: ChangePasswordForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const changePasswordSchema = Yup.object({
  currentPassword: Yup.string().required('Vui lòng nhập mật khẩu cũ'),
  newPassword: Yup.string()
    .required('Vui lòng nhập mật khẩu mới')
    .min(6, 'Mật khẩu mới ít nhất 6 ký tự')
    .notOneOf([Yup.ref('currentPassword')], 'Mật khẩu mới phải khác mật khẩu cũ'),
  confirmPassword: Yup.string()
    .required('Vui lòng xác nhận mật khẩu mới')
    .oneOf([Yup.ref('newPassword')], 'Xác nhận mật khẩu không khớp'),
});

const mapValidationErrors = (error: Yup.ValidationError) => {
  const next: Record<string, string> = {};
  (error.inner || []).forEach((item) => {
    if (item.path && !next[item.path]) {
      next[item.path] = item.message;
    }
  });
  return next;
};

const ChangePasswordModal = ({ open, onClose, onSuccess }: ChangePasswordModalProps) => {
  const { notifyWithPromise } = useNotification();
  const [form, setForm] = useState<ChangePasswordForm>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm(initialForm);
      setErrors({});
      setSubmitting(false);
    }
  }, [open]);

  const validate = async () => {
    setErrors({});

    try {
      await changePasswordSchema.validate(form, { abortEarly: false });
      return true;
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        setErrors(mapValidationErrors(error));
      }
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!(await validate())) return;

    setSubmitting(true);
    try {
      await notifyWithPromise(
        callChangePassword({
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
        }),
        {
          infoMessage: 'Đang đổi mật khẩu...',
          successMessage: 'Đổi mật khẩu thành công!',
          errorMessage: 'Đổi mật khẩu thất bại!',
        },
      );

      onClose();
      onSuccess?.();
    } catch {
      // notification handled centrally
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CommonModal
      open={open}
      onHide={onClose}
      title="Đổi mật khẩu"
      applyButton={<ModalButton icon={<Save className="size-4 mr-1" />} text="Cập nhật" />}
      onApply={handleSubmit}
      dialogClassName="!w-[92vw] md:!w-[32rem]"
    >
      <div className="space-y-4">
        <Input
          label="Mật khẩu cũ"
          type="password"
          allowPasswordToggle
          value={form.currentPassword}
          onChange={(e) => {
            setForm((prev) => ({ ...prev, currentPassword: e.target.value }));
            if (errors.currentPassword) setErrors((prev) => ({ ...prev, currentPassword: '' }));
          }}
          error={errors.currentPassword}
          required
          disabled={submitting}
        />
        <Input
          label="Mật khẩu mới"
          type="password"
          allowPasswordToggle
          value={form.newPassword}
          onChange={(e) => {
            setForm((prev) => ({ ...prev, newPassword: e.target.value }));
            if (errors.newPassword) setErrors((prev) => ({ ...prev, newPassword: '' }));
          }}
          error={errors.newPassword}
          required
          disabled={submitting}
        />
        <Input
          label="Xác nhận mật khẩu"
          type="password"
          allowPasswordToggle
          value={form.confirmPassword}
          onChange={(e) => {
            setForm((prev) => ({ ...prev, confirmPassword: e.target.value }));
            if (errors.confirmPassword) {
              setErrors((prev) => ({ ...prev, confirmPassword: '' }));
            }
          }}
          error={errors.confirmPassword}
          required
          disabled={submitting}
        />
      </div>
    </CommonModal>
  );
};

export default ChangePasswordModal;